import { createClient } from '@supabase/supabase-js';
import { aiDisabledAwareFetch } from './ai/aiDisabled';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
// Verifica se l'URL e la chiave sono validi prima di creare il client
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('[SUPABASE] URL o chiave mancanti:', {
    url: supabaseUrl ? 'presente' : 'mancante',
    key: supabaseAnonKey ? 'presente' : 'mancante'
  });
}

console.log('[REALTIME] Inizializzazione client Supabase');

// Modalità impersonate: la tab impersonate atterra su /impersonate*, oppure ha già attivato
// il marker in sessionStorage. In quel caso il client Supabase usa sessionStorage isolato
// con una chiave UNICA PER TAB (UUID), così:
//
// 1. La tab parent (admin) usa localStorage chiave 'sb-flusia-auth-token'.
// 2. Ogni tab impersonate usa sessionStorage con chiave 'sb-impersonate-<uuid>'.
//    L'UUID è generato al primo boot della tab e persistito nel sessionStorage stesso
//    (scope per tab → UUID diverso in ogni tab impersonate).
// 3. BroadcastChannel di Supabase JS è derivato dallo storageKey → chiavi UUID diverse
//    significano channel diversi → nessuna propagazione di sessione tra tab impersonate
//    parallele E nessuna contaminazione della tab parent.
const isImpersonateContext =
  typeof window !== 'undefined' &&
  (window.location.pathname.startsWith('/impersonate') ||
    window.sessionStorage.getItem('impersonate_active') === '1');

let impersonateStorageKey: string | undefined = undefined;
if (isImpersonateContext && typeof window !== 'undefined') {
  let instanceId = window.sessionStorage.getItem('impersonate_instance_id');
  if (!instanceId) {
    instanceId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem('impersonate_instance_id', instanceId);
  }
  impersonateStorageKey = `sb-impersonate-${instanceId}`;
}

const authStorage = isImpersonateContext ? window.sessionStorage : window.localStorage;
const authStorageKey = isImpersonateContext ? impersonateStorageKey! : 'sb-flusia-auth-token';

// ─── DIAGNOSTIC LOG (rimuovi dopo aver risolto bug impersonate) ──────────────
console.log('[SUPABASE-BOOT]', {
  pathname: typeof window !== 'undefined' ? window.location.pathname : 'no-window',
  isImpersonateContext,
  flusia_impersonate_mode: typeof window !== 'undefined' ? window.sessionStorage.getItem('flusia_impersonate_mode') : null,
  impersonate_active: typeof window !== 'undefined' ? window.sessionStorage.getItem('impersonate_active') : null,
  authStorageKey,
  storageType: authStorage === (typeof window !== 'undefined' ? window.sessionStorage : null) ? 'sessionStorage' : 'localStorage',
  href: typeof window !== 'undefined' ? window.location.href : 'no-window',
  timestamp: new Date().toISOString(),
});
if (isImpersonateContext) {
  console.log('[SUPABASE] Impersonate context detected → isolated session, storageKey:', authStorageKey);
}

// Regular client for normal operations
// Configura esplicitamente per le operazioni realtime seguendo la documentazione più recente
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // detectSessionInUrl: false SEMPRE — anche nella tab impersonate.
    // La tab impersonate usa verifyOtp() esplicito (vedi ImpersonateLanding.tsx) invece
    // di leggere automaticamente dall'URL. Questo evita la cross-tab session contamination
    // che avveniva quando il browser passava da /auth/v1/verify Supabase prima del nostro frontend.
    detectSessionInUrl: false,
    storage: authStorage,
    storageKey: authStorageKey,
    debug: false
  },
  realtime: {
    // Configurazione ottimizzata per messaggistica real-time (bassa latenza)
    timeout: 5000, // 5 secondi - bilanciamento tra performance e latenza per WhatsApp
    params: {
      eventsPerSecond: 10 // Valore più realistico per evitare throttling
    }
  },
  db: {
    schema: 'public'
  },
  // Interruttore IA dell'azienda: un 403 ai_disabled di una edge function mostra il toast.
  global: {
    fetch: aiDisabledAwareFetch
  }
});


// NOTA: In Supabase, la funzionalità realtime dev'essere abilitata dal pannello di controllo
// Non c'è una funzione "setReltime" nell'API ufficiale

console.log('[REALTIME] Client Supabase inizializzato');

// Available storage buckets in the project
export const storageBuckets = {
  GALLERY: 'gallery',
  COMPANY_LOGOS: 'company_logos',
  DOCS: 'documents',
  CONTRACTS: 'contracts',  // Main bucket for contracts (private)
  UPLOADS: 'uploads',      // General-purpose bucket
  PRODUCT_VIDEOS: 'product-videos'  // Videos for products (public, max 100MB)
};

// Helper to know which buckets are private
export const privateBuckets = [storageBuckets.CONTRACTS];

// Helper to get a secure URL for a file (with download token for private buckets)
export const getSecureFileUrl = async (bucketName: string, filePath: string): Promise<string> => {
  // Check if this is a private bucket
  const isPrivateBucket = privateBuckets.includes(bucketName);
  
  if (isPrivateBucket) {
    // For private buckets, create a signed URL with an expiration time
    const { data } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePath, 60 * 60 * 24); // 24 hour expiry
      
    return data?.signedUrl || '';
  } else {
    // For public buckets, just get the public URL
    const { data } = await supabase.storage
      .from(bucketName)
      .getPublicUrl(filePath);
      
    return data?.publicUrl || '';
  }
};

// Helper function to find an available bucket
export const findAvailableStorageBucket = async (preferredBucket: string, fallbackBuckets: string[] = []): Promise<string | null> => {
  const allBuckets = [preferredBucket, ...fallbackBuckets];
  
  for (const bucketName of allBuckets) {
    try {
      // Try a simple operation (listing objects with a limit of 1)
      const { data, error } = await supabase.storage.from(bucketName).list('', { limit: 1 });
      
      if (!error) {
        console.log(`Found available bucket: ${bucketName}`);
        return bucketName;
      }
    } catch (error) {
      console.error(`Error checking bucket ${bucketName}:`, error);
    }
  }
  
  // If no bucket was found, try to create the preferred bucket
  try {
    const created = await createStorageBucket(preferredBucket);
    if (created) {
      return preferredBucket;
    }
  } catch (error) {
    console.error(`Failed to create bucket ${preferredBucket}:`, error);
  }
  
  return null;
};

// Helper function to create a storage bucket using the Supabase Edge Function
export const createStorageBucket = async (bucketName: string, isPublic: boolean = true): Promise<boolean> => {
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/create-bucket`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`
      },
      body: JSON.stringify({ bucketName, isPublic })
    });
    
    const result = await response.json();
    
    if (result.success) {
      console.log(`Successfully created bucket: ${bucketName}`);
      return true;
    } else {
      console.error(`Failed to create bucket: ${result.error}`);
      return false;
    }
  } catch (error) {
    console.error('Error creating bucket:', error);
    return false;
  }
};